/**
 * BoostSignalsPanel — surface likely-boosted posts across tracked
 * competitors on /competitive. Uses views/followers as the boost proxy
 * (calibrated 2026-07-11: Flax & Sweetgreen avg ~6% ratios, Boojee/
 * Harvest/Veronica's avg 100-180% = heavy paid pushers).
 *
 * IMPORTANT: this is an inference, not a Meta confirmation. Labels use
 * "likely" language and the panel leads with a note card explaining
 * the heuristic. Meta Ad Library API integration would let us cross-
 * check and confirm boosts — currently on the roadmap.
 *
 * No new sync — reads existing ig_competitor_posts + ig_competitor_snapshot.
 */
import { useMemo, useState } from 'react';
import { ExternalLink, Flame, Info, Rocket, Sparkles, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/charts/EmptyState';
import { MotionCard } from '@/components/motion/MotionCard';
import { formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';
import { imgProxy } from '@/lib/img';
import {
  useBoostSignals,
  useBoostScoresForHandle,
  tierForScore,
  type BoostSignal,
  type BoostTier,
} from '@/hooks/useBoostSignals';
import type { PostPreviewData } from '@/components/PostPreview';

interface Props {
  onPreview: (p: PostPreviewData) => void;
  /** Restrict to a single competitor (used in per-brand tabs). */
  onlyHandle?: string;
  /** Hide the amber inference note (already shown at page level). */
  hideNote?: boolean;
}

const TIER_META: Record<BoostTier, { label: string; className: string; ring: string }> = {
  likely: {
    label: 'Likely boosted',
    className: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
    ring: 'ring-rose-500/40',
  },
  possible: {
    label: 'Possible boost',
    className: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    ring: 'ring-amber-500/40',
  },
  organic: {
    label: 'Likely organic',
    className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    ring: 'ring-emerald-500/30',
  },
  unknown: {
    label: '—',
    className: 'bg-muted text-muted-foreground',
    ring: 'ring-border/40',
  },
};

export function InferenceNote() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-2xs text-muted-foreground">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
      <div>
        <span className="font-medium text-foreground">Inferred from public IG data, not confirmed by Meta.</span>{' '}
        The score = post views ÷ account followers × 100. Organic reach on
        Instagram caps ~15–20% for most accounts, so scores above 50% are
        almost certainly paid — but a single high score could still be a
        viral hit. Public sources also can't tell an IG-app boost apart
        from a Facebook Ads Manager campaign; both look identical.
        Confirming individual posts needs the Meta Ad Library API (roadmap).
      </div>
    </div>
  );
}

export function BoostSignalsPanel({ onPreview, onlyHandle, hideNote }: Props) {
  const { signals, isLoading } = useBoostSignals(30);
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = useMemo(
    () => (onlyHandle ? signals.filter((s) => s.username === onlyHandle) : signals),
    [signals, onlyHandle],
  );

  const totals = useMemo(() => {
    let likely = 0;
    let possible = 0;
    let organic = 0;
    let posts = 0;
    for (const s of filtered) {
      likely += s.likelyBoosted;
      possible += s.possibleBoost;
      organic += s.organic;
      posts += s.postsWithViews;
    }
    return { likely, possible, organic, posts };
  }, [filtered]);

  const topBooster = filtered[0];

  if (isLoading) {
    return (
      <div className="rounded-md border border-border/40 bg-muted/20 p-6 text-center text-sm text-muted-foreground">
        Analysing boost signals…
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <EmptyState
        title={onlyHandle ? `No boost data yet for @${onlyHandle}` : 'Not enough data to infer boosting'}
        description={
          onlyHandle
            ? 'Needs at least one post with view data + a follower snapshot. Give the sync a run.'
            : 'Need at least one competitor with post views + follower count. Sync competitor data on /competitive first.'
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      {!hideNote && <InferenceNote />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Suspected boosts (30d)"
          value={formatNumberCompact(totals.likely)}
          sub={`of ${totals.posts} posts with view data`}
          tone="rose"
          icon={<Rocket className="h-3.5 w-3.5" />}
        />
        <Stat
          label="Possible boosts"
          value={formatNumberCompact(totals.possible)}
          sub="20–50% views/followers"
          tone="amber"
          icon={<TrendingUp className="h-3.5 w-3.5" />}
        />
        <Stat
          label={onlyHandle ? 'Avg boost score' : 'Heaviest pusher'}
          value={
            onlyHandle
              ? `${topBooster?.avgScore.toFixed(0) ?? 0}%`
              : topBooster
                ? `@${topBooster.username}`
                : '—'
          }
          sub={
            onlyHandle
              ? 'views ÷ followers × 100'
              : topBooster
                ? `avg ${topBooster.avgScore.toFixed(0)}% views/fol`
                : ''
          }
          tone="rose"
          icon={<Flame className="h-3.5 w-3.5" />}
        />
        <Stat
          label="Likely organic"
          value={formatNumberCompact(totals.organic)}
          sub="< 20% views/followers"
          tone="emerald"
          icon={<Sparkles className="h-3.5 w-3.5" />}
        />
      </div>

      <div
        className={cn(
          'grid gap-3',
          onlyHandle ? 'md:grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {filtered.map((s) => (
          <CompetitorBoostCard
            key={s.username}
            signal={s}
            expanded={onlyHandle ? true : expanded === s.username}
            onToggle={() => setExpanded(expanded === s.username ? null : s.username)}
            onPreview={onPreview}
            forceExpanded={!!onlyHandle}
          />
        ))}
      </div>
    </div>
  );
}

function CompetitorBoostCard({
  signal,
  expanded,
  onToggle,
  onPreview,
  forceExpanded,
}: {
  signal: BoostSignal;
  expanded: boolean;
  onToggle: () => void;
  onPreview: (p: PostPreviewData) => void;
  forceExpanded?: boolean;
}) {
  const tier = tierForScore(signal.avgScore);
  const meta = TIER_META[tier];

  return (
    <MotionCard>
      <Card className={cn('overflow-hidden border ring-1', meta.ring)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between gap-2 text-sm">
            <span>@{signal.username}</span>
            <span className={cn('rounded px-1.5 py-0.5 text-2xs font-medium', meta.className)}>
              {meta.label}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="grid grid-cols-2 gap-2 text-2xs">
            <div>
              <div className="text-muted-foreground">Avg boost score</div>
              <div className="mt-0.5 text-xl font-semibold tabular-nums">
                {signal.avgScore.toFixed(0)}%
              </div>
              <div className="text-muted-foreground">
                across {signal.postsWithViews} posts w/ views
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Peak</div>
              <div className="mt-0.5 text-xl font-semibold tabular-nums">
                {signal.maxScore.toFixed(0)}%
              </div>
              <div className="text-muted-foreground">single post</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-1 text-2xs">
            {signal.likelyBoosted > 0 && (
              <span className={cn('rounded-full px-1.5 py-0.5 font-medium', TIER_META.likely.className)}>
                🚀 {signal.likelyBoosted} likely boosted
              </span>
            )}
            {signal.possibleBoost > 0 && (
              <span className={cn('rounded-full px-1.5 py-0.5 font-medium', TIER_META.possible.className)}>
                ~ {signal.possibleBoost} possible
              </span>
            )}
            {signal.organic > 0 && (
              <span className={cn('rounded-full px-1.5 py-0.5 font-medium', TIER_META.organic.className)}>
                {signal.organic} organic
              </span>
            )}
          </div>

          {signal.topBoost && (
            <button
              type="button"
              onClick={() =>
                onPreview({
                  id: signal.topBoost!.postId,
                  caption: signal.topBoost!.caption,
                  permalink: signal.topBoost!.permalink,
                  imageUrl: signal.topBoost!.imageUrl,
                  thumbnailUrl: signal.topBoost!.thumbnailUrl,
                  r2ThumbKey: signal.topBoost!.r2ThumbKey,
                  r2MediaKey: signal.topBoost!.r2MediaKey,
                  mediaType: signal.topBoost!.mediaType,
                  postedAt: signal.topBoost!.postedAt,
                  likes: signal.topBoost!.likes,
                  comments: signal.topBoost!.comments,
                  views: signal.topBoost!.views,
                  isBoosted: signal.topBoost!.score >= 50,
                  source: `@${signal.username}`,
                })
              }
              className="group flex w-full items-center gap-2 rounded-md border border-border/40 bg-card p-2 text-left transition hover:bg-accent"
            >
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border/40 bg-muted">
                <ThumbMedia
                  mediaType={signal.topBoost.mediaType}
                  thumbnailUrl={signal.topBoost.thumbnailUrl}
                  imageUrl={signal.topBoost.imageUrl}
                  r2ThumbKey={signal.topBoost.r2ThumbKey}
                  r2MediaKey={signal.topBoost.r2MediaKey}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-2xs font-medium">Top boost this month</div>
                <div className="mt-0.5 line-clamp-1 text-2xs text-muted-foreground">
                  {(signal.topBoost.caption ?? '').split('\n')[0] || 'No caption'}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-2xs text-muted-foreground tabular-nums">
                  <span>👁 {formatNumberCompact(signal.topBoost.views)}</span>
                  <span className="font-semibold text-rose-500">
                    {signal.topBoost.score.toFixed(0)}%
                  </span>
                </div>
              </div>
              <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground group-hover:text-foreground" />
            </button>
          )}

          {!forceExpanded && (
            <button
              type="button"
              onClick={onToggle}
              className="w-full text-left text-2xs text-muted-foreground hover:text-foreground"
            >
              {expanded ? '▾ Hide post-level scores' : '▸ Show post-level scores'}
            </button>
          )}
          {expanded && <PostLevelScores username={signal.username} onPreview={onPreview} />}
        </CardContent>
      </Card>
    </MotionCard>
  );
}

function PostLevelScores({
  username,
  onPreview,
}: {
  username: string;
  onPreview: (p: PostPreviewData) => void;
}) {
  const { posts, followers, isLoading } = useBoostScoresForHandle(username, 30);
  if (isLoading) {
    return <div className="text-2xs text-muted-foreground">Loading…</div>;
  }
  const withScore = posts
    .filter((p) => p.boostScore != null)
    .sort((a, b) => (b.boostScore ?? 0) - (a.boostScore ?? 0));
  if (withScore.length === 0) {
    return <div className="text-2xs text-muted-foreground">No posts with view data.</div>;
  }
  const top = withScore.slice(0, 10);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-2xs text-muted-foreground">
        <span>Top {top.length} boosted posts</span>
        <span>{formatNumberCompact(followers ?? 0)} followers baseline</span>
      </div>
      <ul className="max-h-96 space-y-1 overflow-y-auto pr-1">
        {top.map((p) => {
          const tierClass = TIER_META[p.tier].className;
          return (
            <li key={p.post_id}>
              <button
                type="button"
                onClick={() =>
                  onPreview({
                    id: p.post_id,
                    caption: p.caption,
                    permalink: p.permalink,
                    imageUrl: p.image_url,
                    thumbnailUrl: p.thumbnail_url,
                    r2ThumbKey: p.r2_thumb_key,
                    r2MediaKey: p.r2_media_key,
                    mediaType: p.media_type,
                    postedAt: p.posted_at,
                    likes: p.like_count,
                    comments: p.comments_count,
                    views: p.views,
                    isBoosted: (p.boostScore ?? 0) >= 50,
                    source: `@${username}`,
                  })
                }
                className="flex w-full items-center gap-2 rounded border border-border/30 bg-card px-2 py-1 text-left text-2xs hover:bg-accent"
              >
                <span className={cn('rounded px-1 py-0.5 font-medium tabular-nums', tierClass)}>
                  {(p.boostScore ?? 0).toFixed(0)}%
                </span>
                <span className="tabular-nums text-muted-foreground">
                  👁 {formatNumberCompact(p.views ?? 0)}
                </span>
                <span className="line-clamp-1 flex-1 text-foreground/80">
                  {(p.caption ?? '').split('\n')[0] || 'No caption'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Reels come back from Supermetrics with thumbnail_url pointing at the .mp4
 * itself (no jpg poster). Rendering that in <img> gives a broken icon.
 * For VIDEO / .mp4 keys we render <video preload=metadata> so the browser
 * paints the first frame as an implicit poster.
 */
function ThumbMedia({
  mediaType,
  thumbnailUrl,
  imageUrl,
  r2ThumbKey,
  r2MediaKey,
}: {
  mediaType: string | null;
  thumbnailUrl: string | null;
  imageUrl: string | null;
  r2ThumbKey: string | null;
  r2MediaKey: string | null;
}) {
  const isVideo = mediaType === 'VIDEO';
  if (isVideo && r2MediaKey) {
    return (
      <video
        src={imgProxy(null, r2MediaKey)}
        muted
        playsInline
        preload="metadata"
        className="h-full w-full object-cover"
      />
    );
  }
  const gridSrc = isVideo ? thumbnailUrl : imageUrl || thumbnailUrl;
  if (!gridSrc || /\.mp4(\?|$)/i.test(gridSrc)) return null;
  return (
    <img
      src={imgProxy(gridSrc, r2ThumbKey ?? (isVideo ? null : r2MediaKey))}
      alt=""
      className="h-full w-full object-cover"
      loading="lazy"
    />
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
  icon,
}: {
  label: string;
  value: string;
  sub: string;
  tone: 'rose' | 'amber' | 'emerald';
  icon: React.ReactNode;
}) {
  const toneClass =
    tone === 'rose'
      ? 'text-rose-500'
      : tone === 'amber'
        ? 'text-amber-500'
        : 'text-emerald-500';
  return (
    <div className="rounded-md border border-border/40 bg-card/60 p-3">
      <div className={cn('flex items-center gap-1.5 text-2xs text-muted-foreground')}>
        <span className={toneClass}>{icon}</span>
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
      <div className="mt-0.5 text-2xs text-muted-foreground">{sub}</div>
    </div>
  );
}
